import { useEffect, useRef, useState } from "react";
import {
  AnimatePresence,
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";

/**
 * Interaktive 3D-Fashion-Galerie für Justins Original-Designs.
 *
 * Architektur: EINE kontinuierliche Index-MotionValue (indexMV, Float) ist die
 * einzige Source of Truth für die räumliche Anordnung. Drag setzt sie direkt,
 * Wechsel = Spring-Animation auf die nächste Ganzzahl. Dadurch:
 * - kein Rücksprung bei Pfeil-Klicks (kein Offset-Reset, keine Kompensation)
 * - pro Geste/Klick wird STRIKT maximal 1 Design gewechselt
 * - Greifen mitten in der Animation ist nahtlos (stop + Ist-Wert)
 *
 * - Drag (Maus) / Swipe (Touch): rotateY zur Mitte, translateZ-Tiefe, Tilt
 * - Leichtes Ziehen + Loslassen → federt sanft zurück
 * - touch-action: pan-y → vertikaler Seiten-Scroll bleibt frei
 * - prefers-reduced-motion: 2D-Slides ohne 3D
 * - KEIN Auto-Wechsel, kein Scroll-Wechsel
 */

const SPRING = { type: "spring", stiffness: 220, damping: 24, mass: 1.1 };
const TWEEN = { duration: 0.35, ease: [0.16, 1, 0.3, 1] };

function GalleryCard({ item, index, indexMV, gap, reduced, testId }) {
  // Kontinuierliche Raum-Position: 0 = Mittelpunkt, ±1 = direkt daneben in der Tiefe
  const pos = useTransform(indexMV, (c) => index - c);
  // Horizontal klar NEBEN der Mitte (keine Überlappung), Tiefe + Rotation zur Mitte hin
  const x = useTransform(pos, (p) => p * gap);
  const y = useTransform(pos, (p) => Math.min(Math.abs(p), 2) * 14);
  const z = useTransform(pos, (p) => (reduced ? 0 : -Math.min(Math.abs(p), 2.4) * 300));
  const rotateY = useTransform(pos, (p) => (reduced ? 0 : Math.max(-28, Math.min(28, p * 26))));
  const rotateZ = useTransform(pos, (p) => (reduced ? 0 : Math.max(-3, Math.min(3, p * -2))));
  const opacity = useTransform(pos, (p) => (Math.abs(p) > 2.2 ? 0 : 1 - Math.min(Math.abs(p), 2) * 0.32));
  const scale = useTransform(pos, (p) => 1 - Math.min(Math.abs(p), 2) * 0.09);
  const brightness = useTransform(pos, (p) => `brightness(${1 - Math.min(Math.abs(p), 2) * 0.3})`);
  const zIndex = useTransform(pos, (p) => 40 - Math.round(Math.abs(p) * 10));

  return (
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
      <motion.figure
        data-testid={testId}
        className="w-[min(72vw,380px)] md:w-[min(32vw,420px)] will-change-transform"
        style={{ x, y, z, rotateY, rotateZ, opacity, scale, zIndex, filter: brightness, transformStyle: "preserve-3d" }}
      >
        <div className="overflow-hidden border border-white/10 bg-[#0a0a0a] shadow-[0_50px_140px_rgba(0,0,0,0.65)]">
          <img
            src={item.src}
            alt={`${item.title} — ${item.meta}`}
            draggable={false}
            className="w-full h-auto block select-none"
          />
        </div>
      </motion.figure>
    </div>
  );
}

export default function DesignGallery({ items }) {
  const [current, setCurrent] = useState(0);
  const [interacted, setInteracted] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [isFinePointer, setIsFinePointer] = useState(true);
  const [spacing, setSpacing] = useState(340);
  const [gap, setGap] = useState(430);
  const indexMV = useMotionValue(0); // kontinuierlicher Deck-Index (Float)
  const reduced = useReducedMotion();
  const stageRef = useRef(null);
  const dragState = useRef(null);
  const currentRef = useRef(0); // ganzzahliger Index — Source of truth für UI/Grenzen

  // Spacing (Drag-Normierung px→Index) + Gap (visueller Abstand = Kartenbreite,
  // damit Nachbarn NEBEN der Mitte stehen statt darin) responsiv messen
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = () => {
      setSpacing(Math.max(220, Math.min(380, el.clientWidth * 0.34)));
      const vw = window.innerWidth;
      const cardW = vw >= 768 ? Math.min(vw * 0.32, 420) : Math.min(vw * 0.72, 380);
      setGap(cardW * 1.06);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  useEffect(() => {
    setIsFinePointer(window.matchMedia("(pointer: fine)").matches);
  }, []);

  const commit = (dir) => {
    setInteracted(true);
    const next = Math.max(0, Math.min(items.length - 1, currentRef.current + dir));
    if (next === currentRef.current) return;
    currentRef.current = next;
    setCurrent(next); // UI (Caption, Pfeile)
    // EIN Wert, EIN Ziel: die Spring-Animation läuft monoton von der aktuellen
    // Float-Position zum Ziel-Index — kein Reset, kein Rücksprung.
    animate(indexMV, next, reduced ? TWEEN : SPRING);
  };

  const onPointerDown = (e) => {
    stageRef.current?.setPointerCapture?.(e.pointerId);
    indexMV.stop(); // laufende Animation einfrieren — Geste startet vom exakten Ist-Zustand
    dragState.current = {
      startX: e.clientX,
      lastX: e.clientX,
      lastT: performance.now(),
      vel: 0,
      base: indexMV.get(),
    };
    setDragging(true);
    setInteracted(true);
  };

  const onPointerMove = (e) => {
    const d = dragState.current;
    if (!d) return;
    const now = performance.now();
    const dt = now - d.lastT;
    if (dt > 0) {
      d.vel = 0.75 * d.vel + 0.25 * (((e.clientX - d.lastX) / dt) * 1000);
      d.lastX = e.clientX;
      d.lastT = now;
    }
    // Nach links ziehen → Index steigt (nächstes Design kommt von rechts)
    let target = d.base - (e.clientX - d.startX) / spacing;
    // Physischer Widerstand an den Enden der Galerie
    if (target < 0) target *= 0.35;
    if (target > items.length - 1) target = items.length - 1 + (target - (items.length - 1)) * 0.35;
    indexMV.set(target);
  };

  const onPointerUp = () => {
    const d = dragState.current;
    if (!d) return;
    dragState.current = null;
    setDragging(false);
    // Gesten-Delta in Index-Einheiten: Links-Zug erhöht den Index (delta < 0 = vorwärts).
    // Pro Swipe wird STRIKT max. 1 Design gewechselt.
    const delta = d.base - indexMV.get();
    const velIdx = -d.vel / spacing;
    if ((delta < -0.28 || velIdx > 1.4) && currentRef.current < items.length - 1) commit(1);
    else if ((delta > 0.28 || velIdx < -1.4) && currentRef.current > 0) commit(-1);
    else animate(indexMV, currentRef.current, reduced ? TWEEN : SPRING); // sanftes Zurückfedern
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft" && currentRef.current > 0) commit(-1);
    if (e.key === "ArrowRight" && currentRef.current < items.length - 1) commit(1);
  };

  if (!items || items.length === 0) return null;

  return (
    <div
      className="sm:col-span-2 lg:col-span-12 relative overflow-hidden border border-black/10 bg-[#050505] px-3 md:px-8 pt-10 md:pt-14 pb-8"
      data-testid="design-gallery-wrap"
    >
      {/* Dunkler Raum: subtile Beleuchtung + Vignette, Designs bleiben der Fokus */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 55% 45% at 50% 38%, rgba(212,175,55,0.10), transparent 70%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse 100% 90% at 50% 50%, transparent 55%, rgba(0,0,0,0.5))",
        }}
      />

      {/* Bühne — Drag/Swipe; vertikaler Seiten-Scroll bleibt frei (pan-y) */}
      <div
        ref={stageRef}
        data-testid="design-gallery"
        role="region"
        aria-label="Clothing-Design-Galerie"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className={`relative h-[min(78vh,620px)] md:h-[min(82vh,720px)] touch-pan-y outline-none focus-visible:ring-1 focus-visible:ring-gold ${
          dragging ? "cursor-grabbing" : "cursor-grab"
        }`}
        style={{ perspective: reduced ? "none" : "1400px" }}
      >
        {items.map((item, i) => (
          <GalleryCard
            key={item.src}
            item={item}
            index={i}
            indexMV={indexMV}
            gap={gap}
            reduced={reduced}
            testId={`portfolio-item-${i}`}
          />
        ))}

        {/* Navigationshinweis — verschwindet nach der ersten Interaktion */}
        {!interacted && (
          <div
            data-testid="gallery-hint"
            className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 pointer-events-none"
          >
            <span className="inline-block text-[10px] uppercase tracking-[0.35em] text-goldlight/90 border border-gold/30 bg-black/50 backdrop-blur px-4 py-2 animate-pulse">
              {isFinePointer ? "Drag to explore" : "Swipe to explore"}
            </span>
          </div>
        )}
      </div>

      {/* Dezente Pfeile (Maus/Tastatur-Alternative zum Drag) */}
      <button
        type="button"
        onClick={() => commit(-1)}
        disabled={current === 0}
        aria-label="Vorheriges Design"
        data-testid="gallery-prev"
        className="absolute left-3 md:left-6 top-[45%] -translate-y-1/2 z-50 w-11 h-11 rounded-full border border-white/15 bg-black/40 backdrop-blur text-bone flex items-center justify-center hover:border-gold hover:text-goldlight transition-colors duration-300 disabled:opacity-0"
      >
        <ArrowLeft size={16} />
      </button>
      <button
        type="button"
        onClick={() => commit(1)}
        disabled={current === items.length - 1}
        aria-label="Nächstes Design"
        data-testid="gallery-next"
        className="absolute right-3 md:right-6 top-[45%] -translate-y-1/2 z-50 w-11 h-11 rounded-full border border-white/15 bg-black/40 backdrop-blur text-bone flex items-center justify-center hover:border-gold hover:text-goldlight transition-colors duration-300 disabled:opacity-0"
      >
        <ArrowRight size={16} />
      </button>

      {/* Caption des aktuellen Designs (bestehender Content: Titel + Meta) */}
      <div className="relative z-10 mt-5 text-center min-h-[54px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={current}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-display uppercase tracking-tight text-lg md:text-xl text-bone" data-testid="gallery-title">
              {items[current].title}
            </p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-1.5" data-testid="gallery-meta">
              {items[current].meta} · {current + 1} / {items.length}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
